import { z } from 'zod';
import { lenient } from '../_shared/decode.js';
const asArray = (value) => Array.isArray(value) ? value : [];
// La réponse fournisseur n'a pas de type exploitable : on décode, nœud par nœud,
// exactement les champs lus ici. Chaque champ est tolérant (`lenient`) parce que
// les deux réponses — shop et price — ne portent pas les mêmes clés, et qu'un
// champ absent doit rester une absence plutôt que d'annuler tout le dump.
const numberField = lenient(z.number());
const stringField = lenient(z.string());
const arrayField = lenient(z.array(z.unknown()));
/** `priceArray[i]` : seul `cost.detailArray` est lu. */
const priceNodeSchema = z
    .object({ cost: z.object({ detailArray: arrayField }).catch({}) })
    .catch({ cost: {} });
/** `detailArray[i]` : les règles tarifaires et les passagers qu'il couvre. */
const detailNodeSchema = z
    .object({
    rate: z.object({ ruleArray: arrayField }).catch({}),
    travellerArray: arrayField,
})
    .catch({ rate: {} });
/** `ruleArray[i]` : la règle bagage et ses bornes. */
const luggageRuleNodeSchema = z
    .object({
    luggage: z
        .object({
        quantity: z.object({ max: numberField }).catch({}),
        weight: z.object({ max: numberField, xUnit: stringField }).catch({}),
        xCategory: stringField,
    })
        .catch({ quantity: {}, weight: {} }),
    xRole: stringField,
})
    .catch({ luggage: { quantity: {}, weight: {} } });
/** Entrée du `travellerArray` racine : porte le type de passager et ses identifiants. */
const travellerNodeSchema = z
    .object({
    xId: stringField,
    personType: stringField,
    signature: stringField,
    travellerKey: stringField,
    xType: stringField,
})
    .catch({});
/** Référence passager posée sur un détail tarifaire. */
const travellerRefSchema = z
    .object({
    signature: stringField,
    xRef: stringField,
    xType: stringField,
    xTypePriced: stringField,
})
    .catch({});
/**
 * Dump de tous les détails tarifaires du 1er `priceArray`, avec leurs règles bagage.
 * Les valeurs finales retenues (`cabin`, `checked`, `accountCode`) sont fournies par
 * l'appelant : elles sortent de son propre pipeline d'extraction, forçages métier compris.
 */
export function buildLuggageDebugSnapshot(raw, resolved) {
    const firstPrice = priceNodeSchema.parse(asArray(raw.priceArray)[0]);
    const detailArray = firstPrice.cost.detailArray ?? [];
    const travellerIndex = buildTravellerTypeIndex(raw.travellerArray);
    const details = detailArray.map((entry, index) => {
        const detail = detailNodeSchema.parse(entry);
        const ruleArray = detail.rate.ruleArray ?? [];
        return {
            index,
            luggageRules: extractLuggageRules(ruleArray),
            ruleCount: ruleArray.length,
            travellerTypes: resolveTravellerTypes(detail.travellerArray ?? [], travellerIndex),
        };
    });
    const snapshot = {
        cabin: resolved.cabin,
        checked: resolved.checked,
        detailCount: details.length,
        details,
        readDetailIndex: 0,
    };
    if (resolved.accountCode)
        snapshot.accountCode = resolved.accountCode;
    return snapshot;
}
/**
 * Rapport plat d'une divergence bagage, prêt à être posé à la racine du wide event.
 *
 * Même jeu de champs aux deux stades : une requête Sentry unique
 * (`action:luggage.divergence`) les couvre, et `luggageStage` permet de les distinguer.
 * Voir {@link LuggageDivergenceReport} pour la contrainte « scalaires uniquement ».
 */
export function buildLuggageDivergenceReport(params) {
    const report = {
        action: 'luggage.divergence',
        luggageActual: params.actual,
        luggageExpected: params.expected,
        luggageRuleFoundAtDetail: params.observed ? findCheckedLuggageDetailIndex(params.observed) : -1,
        luggageStage: params.stage,
    };
    if (params.accountCode)
        report.luggageAccountCode = params.accountCode;
    if (params.airline)
        report.luggageAirline = params.airline;
    if (params.route)
        report.luggageRoute = params.route;
    return report;
}
/**
 * Index du premier détail tarifaire portant une règle soute (`Hold` avec quantité ou
 * poids), `-1` si aucun n'en porte.
 *
 * C'est le champ qui tranche entre les deux causes possibles : `> 0` signifie que le
 * fournisseur a bien renvoyé la règle, mais dans un détail que l'extraction ne lit pas
 * (elle s'arrête à `readDetailIndex`) ; `-1` signifie que la règle est réellement absente
 * de la réponse et que le problème est en amont, chez le fournisseur.
 */
export function findCheckedLuggageDetailIndex(snapshot) {
    return snapshot.details.findIndex((detail) => detail.luggageRules.some((rule) => rule.category === 'Hold' && ((rule.quantityMax ?? 0) > 0 || (rule.weightMax ?? 0) > 0)));
}
/**
 * Vrai lorsque le shop promettait de la soute et que le price n'en retourne aucune —
 * la divergence signalée en production. Le sens inverse (price > shop) n'est pas un
 * incident client : il n'y a rien de promis en moins.
 */
export function hasShopPriceLuggageDivergence(shop, price) {
    if (!shop)
        return false;
    return shop.checked.quantity > 0 && price.checked.quantity === 0;
}
/** Index `xId` → `xType` du `travellerArray` racine, pour résoudre les `xRef` des détails. */
function buildTravellerTypeIndex(travellerArray) {
    const index = new Map();
    for (const entry of asArray(travellerArray)) {
        const traveller = travellerNodeSchema.parse(entry);
        const type = traveller.xType ?? traveller.personType;
        if (!type)
            continue;
        for (const id of [traveller.xId, traveller.travellerKey, traveller.signature]) {
            if (id)
                index.set(id, type);
        }
    }
    return index;
}
function extractLuggageRules(ruleArray) {
    const rules = [];
    for (const entry of asArray(ruleArray)) {
        const rule = luggageRuleNodeSchema.parse(entry);
        if (rule.xRole !== 'Luggage')
            continue;
        rules.push({
            category: rule.luggage.xCategory,
            quantityMax: rule.luggage.quantity.max,
            weightMax: rule.luggage.weight.max,
            weightUnit: rule.luggage.weight.xUnit,
            xRole: 'Luggage',
        });
    }
    return rules;
}
function resolveTravellerTypes(travellerArray, index) {
    return asArray(travellerArray).map((entry) => {
        const ref = travellerRefSchema.parse(entry);
        // Le book-offer porte parfois le type directement sur la référence ; le shop et le
        // price ne portent qu'un `xRef` à résoudre via le `travellerArray` racine.
        const inlineType = ref.xType ?? ref.xTypePriced;
        if (inlineType)
            return inlineType;
        const refId = ref.xRef ?? ref.signature;
        return (refId && index.get(refId)) || 'unknown';
    });
}
